import { ParentSize } from '@visx/responsive'
import { GeckoColorScale } from 'color-scale-kit'
import * as d3 from 'd3'
import { GeckoSummaryStatisticsHistogram } from 'features/dataLayers'
import React from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import { GeckoMeasurementRange } from 'types'

const BackgroundClip = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  overflow: hidden;
  background-color: transparent;
  fill: transparent;
`
const GrayscaleBackgroundClip = styled(BackgroundClip)`
  filter: grayscale(1);
`

const BackgroundContainer = styled.div`
  display: flex;
  width: 100%;
  height: 24px;
  padding: 0;
  margin: 0;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
`

const safeFloor = (value: number) =>
  !Number.isFinite(value) ? undefined : Math.floor(value)

/**
 * Displays a filtered colorscale, with a distribution when histogram data exists.
 * @param cdfId                 The CDF ID which is used to key the SVG patterns
 * @param valueRange            The value range (e.g. from the CDF's data)
 * @param histogram             The histogram (e.g. from the CDF's data)
 * @param fullValueRange        The full range of values to display
 * @param filteredValueRange    Any filtering of the full value range
 * @param selectedColorScale    The preferred color scale for this data
 */
export const DataFilterBackground: React.FC<{
  cdfId: string
  valueRange: GeckoMeasurementRange
  histogram: GeckoSummaryStatisticsHistogram | undefined
  fullValueRange: GeckoMeasurementRange
  filteredValueRange: GeckoMeasurementRange
  selectedColorScale: GeckoColorScale | undefined
}> = ({
  cdfId,
  valueRange: cdfStatsValueRange,
  histogram: cdfStatsHistogram,
  fullValueRange,
  filteredValueRange,
  selectedColorScale,
}) => {
  const { bands, numBands, colorURL } = React.useMemo(() => {
    if (!cdfStatsHistogram?.length)
      return {
        bands: [],
        numBands: 0,
        colorURL: selectedColorScale?.getTextureURL(),
      }

    const numBands = cdfStatsHistogram.length
    const bandWidth =
      cdfStatsValueRange.delta.toNumber(cdfStatsValueRange.unit) / numBands

    const bands: [number, number][] = [...cdfStatsHistogram]

    if (fullValueRange.min.lt(cdfStatsValueRange.min)) {
      const rangeBandStart =
        fullValueRange.min
          .minus(cdfStatsValueRange.min)
          .toNumber(cdfStatsValueRange.unit) / bandWidth
      bands.unshift([rangeBandStart, 0])
    }

    if (fullValueRange.min.gt(cdfStatsValueRange.max)) {
      const rangeBandEnd =
        fullValueRange.max
          .minus(cdfStatsValueRange.max)
          .toNumber(cdfStatsValueRange.unit) / bandWidth
      bands.push([rangeBandEnd, 0])
    }

    return {
      bands: bands,
      numBands: numBands,
      colorURL: selectedColorScale?.getTextureURL(),
    }
  }, [
    cdfStatsHistogram,
    cdfStatsValueRange.delta,
    cdfStatsValueRange.max,
    cdfStatsValueRange.min,
    cdfStatsValueRange.unit,
    fullValueRange.max,
    fullValueRange.min,
    selectedColorScale,
  ])

  const { theme } = useGeckoTheme()

  return (
    <BackgroundContainer>
      <ParentSize>
        {({ width, height }) =>
          width === 0 || height === 0 || !colorURL ? null : (
            <>
              <BackgroundClip
                style={{
                  zIndex: 1,
                  width:
                    safeFloor(
                      ((filteredValueRange.max.num - fullValueRange.min.num) /
                        fullValueRange.size.num) *
                        width
                    ) ?? width,
                  height,
                }}>
                <BackgroundContents
                  bands={bands}
                  numBands={numBands}
                  colorURL={colorURL}
                  width={width}
                  height={height}
                  cdfID={cdfId}
                  selectedColorScale={selectedColorScale}
                />
              </BackgroundClip>
              <GrayscaleBackgroundClip
                style={{
                  zIndex: 1,
                  width:
                    safeFloor(
                      ((filteredValueRange.min.num - fullValueRange.min.num) /
                        fullValueRange.size.num) *
                        width
                    ) ?? 0,
                  height,
                  backgroundColor: theme.forecast.background2,
                }}>
                <BackgroundContents
                  bands={bands}
                  numBands={numBands}
                  colorURL={colorURL}
                  width={width}
                  height={height}
                  cdfID={cdfId}
                />
              </GrayscaleBackgroundClip>
              <GrayscaleBackgroundClip style={{ zIndex: 0, width, height }}>
                <BackgroundContents
                  bands={bands}
                  numBands={numBands}
                  colorURL={colorURL}
                  width={width}
                  height={height}
                  cdfID={cdfId}
                />
              </GrayscaleBackgroundClip>
            </>
          )
        }
      </ParentSize>
    </BackgroundContainer>
  )
}

const BackgroundContents: React.FC<{
  bands: [number, number][]
  numBands: number
  colorURL: string
  width: number
  height: number
  cdfID: string
  selectedColorScale?: GeckoColorScale
}> = ({
  bands,
  numBands,
  colorURL,
  width,
  height,
  cdfID,
  selectedColorScale,
}) => {
  if (bands.length <= 0)
    return (
      <svg width={width} height={height}>
        <image
          href={colorURL}
          preserveAspectRatio='none'
          width={width}
          height={height}
        />
      </svg>
    )

  const x = d3
    .scaleLinear()
    .domain([bands[0][0], numBands - 1])
    .range([0, width])

  const y = d3
    .scaleLinear()
    .domain([0, d3.max(bands, (b) => b[1]) ?? 0])
    .nice()
    .range([height - 1, 1])

  const line = d3
    .line<[number, number]>()
    .x((d) => x(d[0]))
    .y((d) => y(d[1]))
    .curve(d3.curveMonotoneX)

  const area = d3
    .area()
    .x((d) => x(d[0]))
    .y0((d) => y(d[1]))
    .y1(height)
    .curve(d3.curveMonotoneX)

  const d = line(bands)
  const a = area(bands)

  return a && d ? (
    <svg width={width} height={height} fill='url(#background)'>
      <defs>
        <pattern
          id='background'
          patternUnits='userSpaceOnUse'
          width={width}
          height={height}>
          <image
            href={selectedColorScale?.getTextureURL()}
            x={0}
            y={0}
            preserveAspectRatio='none'
            height={height}
            width={width}
          />
        </pattern>
        <pattern
          id={cdfID}
          patternUnits='userSpaceOnUse'
          width={width}
          height={height}>
          <image
            href={colorURL}
            x={0}
            y={0}
            preserveAspectRatio='none'
            height={height}
            width={width}
          />
        </pattern>
      </defs>
      <path
        d={a}
        fill={`url(#${cdfID})`}
        strokeWidth={2.5}
        strokeLinecap='round'
        stroke={`url(#${cdfID})`}
      />
      <path
        d={d}
        fill={`transparent`}
        strokeWidth={2}
        opacity={1}
        strokeLinecap='round'
        stroke={`url(#${cdfID})`}
      />
      <rect
        fill='url(#background)'
        opacity={0.3}
        x1={0}
        x2={width}
        y1={0}
        y2={height}
        height={innerHeight}
        width={innerWidth}
      />
    </svg>
  ) : null
}
